import {BehavioralPopupForm} from '@/components/behavioralPopup/behavioralPopupForm';
import {usePopupContext} from '@/components/PopupProvider';
import {useIsMobile} from '@/hooks/useIsMobile';
import {usePopupAnalytics} from '@/hooks/usePopupAnalytics';
import {submitWaitlist} from '@/server/waitlist';
import {X} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {useCallback, useEffect, useRef, useState} from 'react';

const POPUP_ID = 'behavioral';
const AUTO_DISMISS_MS = 10000;

interface BehavioralPopupProps {
  /** Whether the event-view threshold has been reached */
  triggered: boolean;
}

/**
 * Behavioral email capture popup.
 * Fires after a user views 3+ event listings in a session, signalling active
 * research behavior. Shows a slide-in card on desktop (bottom-right) and a
 * compact bottom bar on mobile. Auto-dismisses after 10 seconds if ignored.
 */
function BehavioralPopup({triggered}: BehavioralPopupProps) {
  const {
    activePopup,
    dismissPopup,
    convertPopup,
    fireBehavioralPopup,
    eventsViewed,
    isFirstSession,
  } = usePopupContext();
  const isMobile = useIsMobile();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isInteracting = isHovered || isFocused;

  const isOpen = activePopup === POPUP_ID;

  const analytics = usePopupAnalytics(POPUP_ID, {eventsViewed, isFirstSession});
  const hasTrackedImpressionRef = useRef(false);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Cancel the auto-dismiss timer without dismissing the popup. */
  const clearAutoDismissTimer = useCallback(() => {
    if (autoDismissTimerRef.current !== null) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  }, []);

  /** Track impression the first time the popup becomes visible. */
  useEffect(() => {
    if (isOpen && !hasTrackedImpressionRef.current) {
      hasTrackedImpressionRef.current = true;
      analytics.trackImpression();
    }
  }, [isOpen, analytics]);

  /**
   * On desktop, auto-fire when the trigger is reached — no competing UI to
   * block. On mobile, the popup is deferred and fired explicitly by the caller
   * (sheet dismissal or event page load) to avoid blocking the action that
   * triggered the threshold.
   */
  useEffect(() => {
    if (triggered && !isOpen && !isMobile) {
      fireBehavioralPopup();
    }
  }, [triggered, isOpen, isMobile, fireBehavioralPopup]);

  /**
   * Auto-dismiss after 10 seconds of inactivity. Pauses while the user has
   * their mouse over the popup or the input focused, and restarts from the
   * full duration once they leave.
   *
   * The timer ID is stored in a ref so manual close/submit handlers can
   * cancel it synchronously, preventing a double-track of Popup Closed.
   */
  useEffect(() => {
    if (!isOpen || isSuccess || isInteracting) return;
    autoDismissTimerRef.current = setTimeout(() => {
      analytics.trackClose('auto-dismiss');
      dismissPopup();
    }, AUTO_DISMISS_MS);
    return () => clearAutoDismissTimer();
  }, [isOpen, isSuccess, isInteracting, dismissPopup, analytics, clearAutoDismissTimer]);

  const interactionHandlers = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  };

  const handleDismiss = useCallback(() => {
    clearAutoDismissTimer();
    dismissPopup();
  }, [dismissPopup, clearAutoDismissTimer]);

  const handleSubmit = useCallback(
    async (email: string) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await submitWaitlist({
          data: {
            email,
            source: 'behavioral-popup',
          },
        });

        if (response.success) {
          clearAutoDismissTimer();
          analytics.trackSubmission();
          setIsSuccess(true);
          setTimeout(() => {
            convertPopup();
          }, 2000);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [convertPopup, analytics, clearAutoDismissTimer],
  );

  const headline = 'Tracking events in your area?';
  const subhead =
    "We'll email you when new races show up near you. No spam. Just events.";

  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{y: '100%'}}
            animate={{y: 0}}
            exit={{y: '100%'}}
            transition={{duration: 0.3, ease: 'easeOut'}}
            className="fixed bottom-0 left-0 right-0 z-[10000] bg-white border-t border-slate-200 shadow-lg px-4 pt-4 pb-6"
            {...interactionHandlers}
          >
            <div className="flex items-start justify-between mb-1">
              <p className="text-base font-semibold text-slate-900 pr-6">
                {headline}
              </p>
              <button
                onClick={() => {
                  analytics.trackClose('x-button');
                  handleDismiss();
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer flex-shrink-0"
                aria-label="Close"
                data-mp-element-name="behavioral-popup-close"
                data-mp-cta-location="behavioral-popup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-3">{subhead}</p>
            <BehavioralPopupForm
              onSubmit={handleSubmit}
              onFormFocus={analytics.trackFormFocus}
              onDismiss={() => {
                analytics.trackClose('decline-link');
                handleDismiss();
              }}
              isSubmitting={isSubmitting}
              error={error}
              isSuccess={isSuccess}
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{x: 40, opacity: 0}}
          animate={{x: 0, opacity: 1}}
          exit={{x: 40, opacity: 0}}
          transition={{duration: 0.3, ease: 'easeOut'}}
          className="fixed bottom-6 right-6 z-50 w-[360px] bg-white rounded-2xl shadow-2xl border border-slate-200 p-6"
          {...interactionHandlers}
        >
          <button
            onClick={() => {
              analytics.trackClose('x-button');
              handleDismiss();
            }}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            aria-label="Close popup"
            data-mp-element-name="behavioral-popup-close"
            data-mp-cta-location="behavioral-popup"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-5">
            <h2 className="font-sans text-lg font-semibold text-slate-900 mb-1 pr-6">
              {headline}
            </h2>
            <p className="text-sm text-slate-600">{subhead}</p>
          </div>

          <BehavioralPopupForm
            onSubmit={handleSubmit}
            onFormFocus={analytics.trackFormFocus}
            onDismiss={() => {
              analytics.trackClose('decline-link');
              handleDismiss();
            }}
            isSubmitting={isSubmitting}
            error={error}
            isSuccess={isSuccess}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export {BehavioralPopup};
